import {
  dateToBookingSchedule,
  parseBookingSchedule
} from '@ps-design-system/components/ScheduleInputField';
import PayIcon from '@ps-design-system/icons/Pay/Pay';
import { addDays } from 'date-fns';
import Cookies from 'react-cookie';
import { checkCateringEligibility } from '../catering/catering-utils';
import { NEW_CARD_OPTION } from './checkout-forms/PaymentForm/CreditCardSelectField';

export const getCurrencyFromListing = (listing) => {
  const prices = listing?.prices || [];
  const currency = prices[0] ? prices[0].currency : 'USD';
  return currency;
};

export const canInstantBook = (booking, listing, totalGuests) => {
  return booking?.is_instant_booking && totalGuests <= listing?.number_guests;
};

export const createAddOnsPayload = (addOns) => {
  return (
    Object.entries(addOns || [])
      // eslint-disable-next-line no-unused-vars
      .filter(([_, count]) => count > 0)
      .map(([itemId, count]) => {
        return {
          type: 'ITEM',
          info: {
            item_id: itemId,
            count
          }
        };
      })
  );
};

export const createQuoteFromContractsResponse = (response) => {
  if (!response) {
    return {
      masterContract: null,
      splitPaymentPermitted: false,
      spaceRentalContract: null,
      addOnsContracts: [],
      guestPaymentDetails: null
    };
  }

  const spaceRentalContract = (response?.children || []).find(
    (item) => item.type === 'SPACE_RENTAL'
  );
  const addOnsContracts = (response?.children || []).filter(
    (contract) => contract.type === 'ITEM'
  );
  const quote = {
    masterContract: response?.master,
    splitPaymentPermitted: response?.split_payment_permitted,
    guestPaymentDetails: response?.guest_payment_method_details,
    spaceRentalContract,
    addOnsContracts
  };

  return quote;
};

export const createQuotePayload = ({ formValues, listing, SSOid }) => {
  const payload = {
    type: 'REQUEST',
    host_sso_id: listing.ownerId,
    guest_sso_id: SSOid,
    guest_payment_ps_credit_amount:
      formValues?.payment?.totalCreditsApplied ?? 0,
    split_payment_checkout:
      formValues?.payment?.selectedPaymentOption === 'pay_partial',
    items: [
      {
        type: 'BOOKING',
        info: {
          guest_id: SSOid,
          host_id: listing.ownerId,
          listing_id: listing._id,
          alcohol_consumed: formValues?.bookingDetails?.alcoholConsumed,
          schedule: formValues?.bookingDetails?.schedule,
          number_of_guests: formValues?.bookingDetails?.totalGuests,
          booking_use_message: formValues?.bookingDetails?.message,
          attr_url: Cookies.load('PSAttrUrl'),
          attr_referrer: Cookies.load('PSAttrReferrer'),
          tags: {
            activity: formValues?.bookingDetails?.activity
          }
        }
      },
      ...createAddOnsPayload(formValues?.bookingDetails?.addOns)
    ]
  };
  return payload;
};

export const createCheckoutFormDefaultValues = ({
  quote,
  inventory,
  customerPaymentMethods,
  customerBillingAddress
}) => {
  const { spaceRentalContract, addOnsContracts } = quote || {};
  const { booking, masterContract } = spaceRentalContract?.meta || {};

  const bookingDetails = {
    schedule: parseBookingSchedule(booking?.schedule || []),
    activity: booking?.tags?.activity?.toLowerCase() || '',
    totalGuests: String(booking?.number_of_guests || ''),
    alcoholConsumed: booking?.alcohol_consumed ?? null,
    message: '', // message always starts blank on checkout page
    hostRulesAccepted: false,
    addOns: (inventory || []).reduce((acc, item) => {
      const addOnFromQuote = addOnsContracts?.find(
        (contract) => contract.item_id === item._id
      );

      acc[item._id] = addOnFromQuote?.units || 0;
      return acc;
    }, {})
  };

  const payment = {
    selectedCreditCard: customerPaymentMethods?.[0] || NEW_CARD_OPTION,
    selectedPaymentOption: masterContract?.split_payment_checkout
      ? 'pay_partial'
      : 'pay_full'
  };

  const billingAddress = {
    country: customerBillingAddress?.country || 'US',
    state: customerBillingAddress?.state,
    city: customerBillingAddress?.city,
    address1: customerBillingAddress?.street,
    zip: customerBillingAddress?.zip
  };

  return { bookingDetails, payment, billingAddress };
};

export const createBookingRequestPayload = ({
  booking,
  formValues,
  paymentMethodId,
  paymentIntentId,
}) => {
  const bookingItem = {
    type: 'BOOKING',
    info: {
      guest_id: booking?.guest_id,
      host_id: booking?.host_id,
      space_id: booking?.space_id,
      listing_id: booking?.listing_id,
      duration_type: booking?.duration_type,
      subtotal_amount: booking?.subtotal_amount,
      schedule: booking?.schedule,
      ga_client_id: booking?.ga_client_id,
      are_host_rules_accepted: formValues?.bookingDetails.hostRulesAccepted,
      alcohol_consumed: formValues?.bookingDetails.alcoholConsumed,
      booking_use_message: formValues?.bookingDetails.message,
      number_of_guests: parseInt(formValues?.bookingDetails.totalGuests, 10),
      tags: {
        activity: formValues?.bookingDetails.activity
      },
      identity_verification:
        formValues?.identityVerification?.identityVerificationStatus,
      guest_social_events_policy_accepted:
        formValues?.socialEventPolicy?.socialEventPolicyAccepted,
      guest_payment_card_id: paymentMethodId,
      attr_url: Cookies.load('PSAttrUrl'),
      attr_referrer: Cookies.load('PSAttrReferrer')
    }
  };

  const requestPayload = {
    type: booking?.obj_state === 'INQUIRED' ? 'INQUIRY' : 'REQUEST',
    host_sso_id: booking?.host_id,
    guest_sso_id: booking?.guest_id,
    guest_payment_ps_credit_amount:
      formValues?.payment?.selectedPaymentOption === 'pay_full'
        ? formValues?.payment?.totalCreditsApplied ?? 0
        : 0,
    split_payment_checkout:
      formValues?.payment?.selectedPaymentOption === 'pay_partial',
    // Forter token is exposed from mustache template
    fc_token: window.fcToken,
    payment_intent_id: paymentIntentId,
    items: [
      bookingItem,
      ...(createAddOnsPayload(formValues?.bookingDetails?.addOns) || [])
    ]
  };

  return requestPayload;
};

export const createIntentRequestPayload = ({
  listing,
  booking,
  formValues,
  paymentMethodId,
  quote
}) => {
  const currency = getCurrencyFromListing(listing);
  const country = listing?.country;
  const amount = getPaymentAmount(quote, formValues?.payment?.selectedPaymentOption);
  const isInstantBooking = canInstantBook(booking, listing, formValues?.bookingDetails.totalGuests);

  return {
    amount,
    currency,
    country,
    payment_method: paymentMethodId,
    is_instant_booking: isInstantBooking
  };
};

export const getPaymentAmount = (quote, selectedPaymentOption) => {
  const { masterContract, guestPaymentDetails } = quote || {};

  if (selectedPaymentOption === 'pay_full') {
    return guestPaymentDetails?.subtotal_remaining ?? 0;
  }

  const splitPaymentBreakdown =
    masterContract?.calculations?.guest?.split_payment || {};

  return splitPaymentBreakdown?.parts?.[0] ?? 0;
};

export const getCheckoutRedirectUrl = (locale, booking, { metro, country }) => {
  const shouldDirectToCatering = checkCateringEligibility(
    metro,
    booking?.number_of_guests || 0,
    booking?.start
  );

  let redirectUrl = '';
  if (shouldDirectToCatering) {
    redirectUrl = `/inbox/${booking?._id}/catering`;
  } else if (booking?.is_instant_booking) {
    redirectUrl = `/invite/add/${booking?._id}`;
  } else {
    redirectUrl =
      (country || '').toLowerCase() !== 'us' || locale !== 'en-US'
        ? `/inbox/${booking?._id}`
        : `/referrals/promotion/${booking?._id}`;
  }

  return redirectUrl;
};

export const getErrorMsg = (error, intl) => {
  const genericErrorMsg = intl.formatMessage({
    id: 'checkout.generic_error',
    defaultMessage: 'An error occurred. Please refresh the page and try again.'
  });

  if (!error) {
    return genericErrorMsg;
  }

  if (typeof error === 'string') {
    return error;
  }

  if (typeof error?.error === 'string') {
    return error.error;
  }

  if (typeof error?.message === 'string') {
    return error.message;
  }

  return genericErrorMsg;
};

export const BOOKING_DETAILS_FORM_DEV_DEFAULTS = {
  activity: 'Event',
  totalGuests: '25',
  alcoholConsumed: false,
  message: 'Hey there hi there ho there',
  hostRulesAccepted: true,
  addOns: {},
  schedule: [
    {
      start: {
        date: dateToBookingSchedule(addDays(new Date(), 7)),
        time: { time_index: 6, time: '9:00 am', h: 9, m: 0, overnight: false }
      },
      end: {
        date: dateToBookingSchedule(addDays(new Date(), 7)),
        time: { time_index: 22, time: '5:00 pm', h: 17, m: 0, overnight: false }
      }
    }
  ]
};

export const getMethodMetaById = (id, intl) => {
  return {
    card: {
      label: intl.formatMessage({ defaultMessage: 'Credit or Debit Card', id: 'checkout.credit_or_debit_card' }),
      Icon: PayIcon,
      testingId: 'cc-option'
    },
    applePay: {
      label: 'Apple Pay',
      image: 'https://assets.peerspace.com/apple-pay-mark.svg?v=rb',
      testingId: 'apple-pay-option'
    },
    googlePay: {
      label: 'Google Pay',
      image: 'https://assets.peerspace.com/google-pay-mark.png?v=rb',
      testingId: 'google-pay-option'
    }
  }[id];
};

export const StripePaymentElementFonts = [
  {
    family: 'ABCSocial',
    src: "url('https://fonts.peerspace.com/ABCSocial/ABCSocial-Book.otf')",
    weight: 350
  },
  {
    family: 'ABCSocial',
    src: "url('https://fonts.peerspace.com/ABCSocial/ABCSocial-Medium.otf')",
    weight: 500
  }
];

export const StripePaymentElementStyles  = {
  theme: 'stripe',
  variables: {
    borderRadius: '4px',
    fontSizeBase: '16px',
    fontSizeSm: '12px',
    fontWeightNormal: 350,
    fontFamily: 'ABCSocial, Helvetica Neue, Helvetica, Arial, sans-serif',
    colorTextPlaceholder: '#6A6E7A',
    spacingGridRow: '24px',
    colorPrimary: '#6c30d1',
    colorDanger: '#e52029',
    colorText: '#121212',
    colorIconTabSelected: '#6c30d1'
  },
  rules: {
    '.Input': {
      border: '1px solid #D1D3DB',
      boxShadow: 'none',
      padding: '8px 12px',
      lineHeight: '24px',
      borderRadius: '4px'
    },
    '.Input:focus': {
      border: '1px solid #121212',
      outline: '2px solid #0E6EC8',
      outlineOffset: '1px',
      boxShadow: 'none'
    },
    '.Input--invalid' : {
      boxShadow: 'none',
      color: '#121212'
    },
    '.Label': {
      fontSize: '16px',
      fontFamily: 'ABCSocial, Helvetica Neue, Helvetica, Arial, sans-serif',
      lineHeight: '24px',
      marginBottom: '4px',
      color: '#121212',
      fontWeight: 500
    },
    '.Error': {
      fontWeight: 350,
      lineHeight: '16px',
      marginTop: '8px'
    }
  }
};
